import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'
import {
  BILLING_RULES,
  SEND_CHANNEL_VERB,
  billToLine,
  billingRuleChoice,
  deriveWorklistStatus,
  describeSendTracking,
  documentPdfState,
  documentSendState,
  jobContactDuties,
  sendRecipient,
  type BillingRuleKey,
  type DocumentSendLike,
  type DocumentSendState,
} from '@fieldops/shared'
import { cn } from '../../lib/cn.js'
import { formatDate, money } from '../../lib/format.js'
import { stageOf } from '../../lib/job-stages.js'
import { useIsNarrow } from '../../lib/media.js'
import { MarkSentPopup } from './MarkSentPopup.js'
import { RaiseDocumentPopup } from './RaiseDocumentPopup.js'
import { CatalogLineField } from './CatalogLineField.js'
import { CatalogItemPopup, type CatalogItem } from '../catalog/CatalogItemPopup.js'
import { ProposalBuilder } from '../builders.js'
import {
  AcceptancePanel,
  DeclinedNote,
  OfficeAcceptPopup,
  ProposalLines,
  ViewedNote,
  acceptNeedsChoice,
  isProposalQuote,
  proposalRailTotals,
} from './ProposalPanels.js'
import { SendDocumentPopup, type SendAddressee } from './SendDocumentPopup.js'
import { useSession } from '../../lib/session.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../../lib/trpc.js'
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  INVOICE_STATUS_TONE,
  QUOTE_STATUS_TONE,
  Select,
  type Tone,
  SkeletonRows,
  buttonClass,
  humanise,
} from '../ui.js'
import { FileLink, attachmentViewerFile, useFileViewer, type ViewerFile } from '../FileViewer.js'
import { Icon } from '../Icon.js'
import { Label, Panel, PanelHeading } from './panes.js'
import { PaymentPopup } from './PaymentPopup.js'
import type { Job } from './types.js'

/**
 * Who a quote or invoice offers to send to, with the billing contact selected.
 * Billing's rows send through this too, so a send from the list and from the
 * card go to the same people.
 */
export function sendAddressee(job: Job): SendAddressee {
  const billing = jobContactDuties(job.contacts).billing

  return {
    recipients: job.contacts.flatMap((link) => {
      const email = link.contact.email
      if (!email) return []
      const name = [link.contact.firstName, link.contact.lastName].filter(Boolean).join(' ')
      return [{ name, email }]
    }),
    fallbackTo: billing?.contact.email ?? job.client.email,
    jobId: job.id,
    files: job.attachments.map((file) => ({ id: file.id, name: file.filename })),
  }
}

/**
 * What was quoted and what was billed.
 *
 * `jobs.byId` returns quote and invoice headers but no lines, so the tables
 * come from `quotes.byId` and `invoices.byId` — fetched only once this tab is
 * open, because most people opening a job from the board never look at it and
 * two extra round trips on every booking they glance at is a poor trade.
 *
 * Only the latest of each is shown. A job with three superseded quotes on it
 * is a job where one of them is the live number and the rest are history, and
 * history belongs in the activity column, which already lists them.
 *
 * Lines are edited here: description, quantity, unit price, add and remove.
 * The Item box searches our catalog (`CatalogLineField`) and can add to it;
 * an item's code is drawn under the line and never printed. A paid invoice
 * stays read-only — a credit note is the instrument once money has been
 * received.
 *
 * On a desk the quote mockup wins: lines in the details column, totals and
 * the next action in a billing rail where the feed sat. The activity column
 * is only on Work (decision 52, amended), so this tab does not keep a note
 * composer beside a price. On a phone there is no rail — totals stay in the
 * foot of the document, the way they always did, because a second column
 * under 1023px is the sheet tree and not this one.
 *
 * Each document is one card: the number and its state along the top, the
 * lines through the middle. The mockup's line is a name and an amount; the
 * quantity, the unit price and whether the line carried tax stay, because on
 * a real invoice they are the three things somebody rings up to argue about.
 */
export function BillingPane({
  job,
  onBook,
}: {
  job: Job
  /** Opens the card's booking window, for the step after a quote is accepted. */
  onBook?: () => void
}) {
  const { can } = useSession()
  const split = !useIsNarrow()

  const quote = job.quotes.find((row) => row.status !== 'SUPERSEDED') ?? job.quotes[0]
  const invoice = job.invoices.find((row) => row.status !== 'VOID') ?? job.invoices[0]
  // Moving the stage back to Quote does not remove an invoice, and the empty
  // state — the only place "Write a proposal" used to live — never renders
  // once one exists. A quote already on the job keeps "Make it a proposal".
  const offerProposal = stageOf(job.status) === 'QUOTE' && !quote && can('QUOTE_EDIT')

  if (!quote && !invoice) {
    return (
      <BillingShell>
        <BillTo job={job} />
        <Panel>
          <NothingBilled job={job} />
        </Panel>
      </BillingShell>
    )
  }

  const showQuote = Boolean(quote && can('QUOTE_VIEW'))
  const showInvoice = Boolean(invoice && can('INVOICE_VIEW'))

  // Sold, quoted, and not billed: the one state the tab used to have no
  // button for. A job past Quote with a live quote and no invoice leads with
  // invoicing what the customer took, the server's `convertToInvoice`, and a
  // blank invoice stays beside it. Decision 103.
  //
  // Accepted and nothing booked is the other dead end: the tab showed the
  // signed quote and View the PDF. The work order's next move is a booking,
  // so that leads, and invoicing the quote waits beside it in secondary
  // buttons until the job is complete.
  const complete = stageOf(job.status) === 'COMPLETE'
  const bookNext =
    onBook && quote?.status === 'ACCEPTED' && deriveWorklistStatus(job) === 'UNSCHEDULED' ? (
      <ReadyToBook onBook={onBook} placement={split ? 'rail' : 'column'} />
    ) : null
  const invoiceFromQuote =
    !invoice && quote && quote.status !== 'SUPERSEDED' && stageOf(job.status) !== 'QUOTE' && can('INVOICE_EDIT') ? (
      <InvoiceFromQuote
        job={job}
        quote={quote}
        placement={split ? 'rail' : 'column'}
        lead={complete || !bookNext}
      />
    ) : null
  const quoteLead =
    bookNext || invoiceFromQuote ? (
      <>
        {bookNext}
        {invoiceFromQuote}
      </>
    ) : null

  // A tab that renders nothing at all reads as broken. Most people on this
  // system are field staff whose role does not include pricing, and they are
  // owed a sentence rather than an empty panel.
  if (!showQuote && !showInvoice) {
    return (
      <BillingShell>
        <Panel>
          <EmptyState
            title="Not your area"
            description="This job has been quoted or invoiced, but your role does not include seeing prices."
          />
        </Panel>
      </BillingShell>
    )
  }

  // The invoice is the live money when both exist. The quote stays on the
  // left as history and keeps its own foot totals, because the rail is the
  // invoice's and a second total beside it would be two answers.
  //
  // Unless the job is in Quote and has never been quoted. Then the rail leads
  // with the proposal, and the invoice's own action steps back so the green
  // button is the one the stage is asking for. On a phone there is no rail,
  // so the same control sits above the invoice.
  return (
    <BillingShell
      rail={
        split ? (
          showInvoice && invoice ? (
            <InvoiceRail
              invoiceId={invoice.id}
              addressee={sendAddressee(job)}
              proposal={offerProposal ? <WriteProposal job={job} placement="rail" /> : null}
            />
          ) : quote ? (
            <QuoteRail quoteId={quote.id} addressee={sendAddressee(job)} lead={quoteLead} />
          ) : null
        ) : null
      }
    >
      <BillTo job={job} />
      {offerProposal && !split ? <WriteProposal job={job} placement="column" /> : null}
      {!split ? quoteLead : null}
      {showQuote && quote ? (
        <QuoteBlock
          quoteId={quote.id}
          railTakesTotals={split && !showInvoice}
          quoting={stageOf(job.status) === 'QUOTE'}
          addressee={sendAddressee(job)}
        />
      ) : null}
      {showInvoice && invoice ? (
        <InvoiceBlock
          invoiceId={invoice.id}
          railTakesTotals={split}
          yieldToProposal={offerProposal && !split}
          addressee={sendAddressee(job)}
        />
      ) : null}
    </BillingShell>
  )
}

/**
 * Who pays for this job, on a client under a parent (decision 123): the
 * bill-to in one line, whose setting that is, and a choice for this job
 * alone. The job's draft invoice follows a change; an issued one keeps what
 * it says. A client with no parent bills itself, so it shows nothing.
 */
function BillTo({ job }: { job: Job }) {
  const { can } = useSession()
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const billing = useQuery({
    ...trpc.clients.billing.queryOptions({ id: job.client.id, jobId: job.id }),
    enabled: can('INVOICE_VIEW'),
  })
  const change = useMutation(
    trpc.invoices.setJobBillingRule.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.clients.billing.pathFilter())
        void queryClient.invalidateQueries(trpc.invoices.pathFilter())
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
      },
    }),
  )

  const data = billing.data
  if (!data?.parent) return null

  const client = job.client.name
  const parent = data.parent.name
  const inherited: BillingRuleKey = data.ownRule ?? data.parentRule ?? 'SELF'
  const whose =
    data.source === 'JOB' ? 'This job only' : data.source === 'CLIENT' ? `${client}’s setting` : `${parent}’s setting`

  return (
    <Panel>
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
        <Label>Bill to</Label>
        <span className="text-sm font-medium text-ink-900">{billToLine(data.rule, client, parent)}</span>
        <span className="text-xs text-ink-500">{whose}</span>
        {can('INVOICE_EDIT') ? (
          <Select
            aria-label="Who pays for this job"
            value={data.jobRule ?? ''}
            disabled={change.isPending}
            onChange={(event) =>
              change.mutate({
                jobId: job.id,
                billingRule: event.target.value === '' ? null : (event.target.value as BillingRuleKey),
              })
            }
            className="ml-auto w-auto max-w-full"
          >
            <option value="">
              As {data.ownRule ? client : parent} says: {billToLine(inherited, client, parent)}
            </option>
            {BILLING_RULES.map((rule) => (
              <option key={rule} value={rule}>
                {billingRuleChoice(rule, client, parent)}, this job only
              </option>
            ))}
          </Select>
        ) : null}
      </div>
      {change.error ? <p className="text-xs font-medium text-red-700">{errorMessage(change.error)}</p> : null}
    </Panel>
  )
}

/**
 * The two columns of the billing tab on a desk; a stack on a phone.
 *
 * JobCard hands this pane the whole body when the tab is open, so the rail
 * sits where the activity column sat rather than nesting inside Job details.
 */
function BillingShell({ children, rail }: { children: ReactNode; rail?: ReactNode }) {
  const narrow = useIsNarrow()

  if (narrow) return <>{children}</>

  return (
    <>
      <section
        aria-label="Job details"
        className={cn(
          'flex min-w-0 flex-1 flex-col px-5 py-1 lg:overflow-y-auto',
          rail ? 'lg:border-r lg:border-ink-200' : null,
        )}
      >
        {children}
      </section>
      {rail}
    </>
  )
}

/**
 * The first document on a job, which until now could not be raised at all.
 *
 * `quotes.create` and `invoices.create` have existed since the schema did and
 * had no caller anywhere in the client: every quote in the system arrived from
 * ServiceM8, a seed or a test. So this is the whole entry point.
 *
 * **The job's stage decides which one leads.** A job in Quote is being priced
 * and wants a quote; a Work Order or a finished job is being billed and wants
 * an invoice. The other stays available as a quieter action rather than hidden,
 * because a job that walked in as a work order still sometimes needs a price in
 * writing, and hiding it would leave the office with no screen to do that on.
 */
function NothingBilled({ job }: { job: Job }) {
  const { can } = useSession()
  // A quote opens the proposal builder, which offers the plain price list as
  // its "quick quote"; that one is still `RaiseDocumentPopup`.
  const [raising, setRaising] = useState<'quote' | 'quick-quote' | 'invoice' | null>(null)

  // LEAD, QUOTING and QUOTED are the Quote stage. Everything else is work
  // that has been sold, so the money owed is the live question.
  const quoting = stageOf(job.status) === 'QUOTE'

  const mayQuote = can('QUOTE_EDIT')
  const mayInvoice = can('INVOICE_EDIT')

  if (!mayQuote && !mayInvoice) {
    return (
      <EmptyState
        title="Nothing billed yet"
        description="No quote and no invoice on this job, and pricing is not part of your role."
      />
    )
  }

  const lead: 'quote' | 'invoice' = quoting && mayQuote ? 'quote' : mayInvoice ? 'invoice' : 'quote'
  const other = lead === 'quote' ? 'invoice' : 'quote'
  const otherAllowed = other === 'quote' ? mayQuote : mayInvoice

  return (
    <>
      <EmptyState
        title="Nothing billed yet"
        description={
          lead === 'quote'
            ? 'This job is being quoted. Put the price in writing and send it.'
            : 'No quote or invoice on this job yet. Raise the invoice for the work, or quote it first.'
        }
        action={
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button variant="primary" onClick={() => setRaising(lead)}>
              {lead === 'quote' ? 'Write a proposal' : 'Raise an invoice'}
            </Button>
            {otherAllowed ? (
              <Button variant="ghost" onClick={() => setRaising(other)}>
                {other === 'quote' ? 'Quote it instead' : 'Invoice it instead'}
              </Button>
            ) : null}
          </div>
        }
      />

      <RaiseDocumentPopup
        kind={raising === 'quick-quote' ? 'quote' : 'invoice'}
        job={job}
        open={raising === 'invoice' || raising === 'quick-quote'}
        onClose={() => setRaising(null)}
      />
      <ProposalBuilder
        kind="quote"
        job={job}
        open={raising === 'quote'}
        onClose={() => setRaising(null)}
        onQuickQuote={() => setRaising('quick-quote')}
      />
    </>
  )
}

/**
 * Starting a proposal on a job that already has an invoice.
 *
 * The empty billing tab is the other door. This one is for a job moved back
 * to Quote after a draft invoice was raised: the invoice stays, and this is
 * the action the stage is now asking for.
 */
function WriteProposal({ job, placement }: { job: Job; placement: 'rail' | 'column' }) {
  const [raising, setRaising] = useState<'quote' | 'quick-quote' | null>(null)
  const copy = 'This job is being quoted. Put the price in writing and send it.'

  return (
    <>
      {placement === 'rail' ? (
        <div className="flex flex-col gap-2">
          <p className="text-[13px] leading-relaxed text-ink-600">{copy}</p>
          <Button variant="primary" block onClick={() => setRaising('quote')}>
            Write a proposal
          </Button>
        </div>
      ) : (
        <Panel>
          <p className="text-[13px] leading-relaxed text-ink-700">{copy}</p>
          <div className="mt-3">
            <Button variant="primary" onClick={() => setRaising('quote')}>
              Write a proposal
            </Button>
          </div>
        </Panel>
      )}
      <RaiseDocumentPopup
        kind="quote"
        job={job}
        open={raising === 'quick-quote'}
        onClose={() => setRaising(null)}
      />
      <ProposalBuilder
        kind="quote"
        job={job}
        open={raising === 'quote'}
        onClose={() => setRaising(null)}
        onQuickQuote={() => setRaising('quick-quote')}
      />
    </>
  )
}

/**
 * Whether the customer is holding what is on the screen.
 *
 * The one sentence this whole feature exists to be able to write. `sentAt`
 * could only ever say *that* something was sent; the send rows say which
 * version, so an invoice edited after it went out stops claiming to be sent.
 */
function SendState({
  state,
  kind,
}: {
  state: DocumentSendState
  kind: 'quote' | 'invoice'
}) {
  if (state.status === 'never') return null

  if (state.status === 'unknown') {
    return (
      <p className="text-xs text-ink-500">
        Sent before this version tracking existed, so which copy the customer
        has is not recorded.
      </p>
    )
  }

  const latest = state.latest!

  if (state.status === 'changed') {
    return (
      <div
        role="status"
        className="flex flex-col gap-1 rounded-md border-l-[3px] border-ink-900 bg-ink-50 px-3 py-2"
      >
        <p className="text-[12.5px] font-semibold text-ink-900">
          Changed since it was sent
        </p>
        <p className="text-xs text-ink-700">
          The customer has v{latest.version}, {SEND_CHANNEL_VERB[latest.channel]}{' '}
          {formatDate(latest.sentAt)}. This {kind} has been edited since — send it again so
          they have the current figures.
        </p>
      </div>
    )
  }

  return (
    <p className="text-xs text-ink-500">
      The customer has this version — v{latest.version},{' '}
      {SEND_CHANNEL_VERB[latest.channel]} {formatDate(latest.sentAt)}
      {latest.sentBy ? ` by ${latest.sentBy.name}` : ''}.
    </p>
  )
}

/**
 * What the customer did with the newest copy of an invoice, beside its totals.
 *
 * A quote says this with `ViewedNote`, from its links. An invoice's link lives
 * on its send, so this reads the newest one. Decision 88.
 */
function LatestSendTracking({ sends }: { sends: DocumentSendLike[] }) {
  const latest = [...sends].sort((a, b) => b.version - a.version)[0]
  const tracking = latest ? describeSendTracking(latest, formatDate) : null
  if (!tracking) return null

  return (
    <p className={cn('text-xs', tracking.problem ? 'font-medium text-red-700' : 'text-ink-600')}>
      {tracking.text}
    </p>
  )
}

/**
 * When each copy went, to whom, by whom, and the exact PDF that went.
 *
 * The version number is the load-bearing part: "sent again" without one does
 * not tell anybody which figures the customer is looking at.
 */
function SendHistory({ sends }: { sends: DocumentSendLike[] }) {
  const { open, viewer } = useFileViewer()
  if (sends.length === 0) return null

  // Every copy that went, so the viewer steps v1 → v2 to compare them.
  const copies = sends.flatMap((send) =>
    send.attachment
      ? [{ version: send.version, file: sentCopyFile(send.attachment, send.version) }]
      : [],
  )
  const files = copies.map((copy) => copy.file)

  return (
    <Panel>
      {viewer}
      <PanelHeading
        title="Sent to the customer"
        hint={sends.length === 1 ? 'Once' : `${sends.length} times`}
      />
      <ul className="flex flex-col divide-y divide-ink-100">
        {sends.map((send) => {
          const copy = copies.find((entry) => entry.version === send.version)?.file
          const to = sendRecipient(send)
          const tracking = describeSendTracking(send, formatDate)

          return (
            <li key={`${send.version}`} className="flex flex-col gap-0.5 py-2 text-[13px]">
              <span className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-mono text-xs font-semibold text-ink-600">
                  v{send.version}
                </span>
                <span className="text-ink-800">
                  {SEND_CHANNEL_VERB[send.channel]}
                  {to ? ` to ${to}` : ''}
                </span>
                <span className="ml-auto shrink-0 tabular-nums text-xs text-ink-500">
                  {formatDate(send.sentAt)}
                </span>
              </span>
              <span className="flex flex-wrap items-baseline gap-x-2 text-xs text-ink-500">
                {send.sentBy ? <span>by {send.sentBy.name}</span> : null}
                {copy ? (
                  <FileLink
                    file={copy}
                    onOpen={(file) => open(files, file.id)}
                    className="font-medium text-ink-700 underline decoration-ink-300 hover:text-ink-900"
                  >
                    The copy they got
                  </FileLink>
                ) : null}
              </span>
              {tracking ? (
                <span
                  className={cn(
                    'text-xs',
                    tracking.problem ? 'font-medium text-red-700' : 'text-ink-600',
                  )}
                >
                  {tracking.text}
                </span>
              ) : null}
            </li>
          )
        })}
      </ul>
    </Panel>
  )
}

/**
 * The stored PDF: save one, open it, or draw it again when it is out of date.
 *
 * Opening is an anchor wearing the button skin, not a `Button` with an
 * onClick: it has to middle-click into a new tab, which is what the office
 * does when it wants the figures beside the screen while it rings somebody.
 *
 * Whether the PDF is current is `documentPdfState` — the hash the render was
 * drawn from against the document's own — so an invoice edited after it was
 * issued offers **Update the PDF** rather than opening yesterday's figures as
 * if they were today's. Saving sends nothing. Decision 88.
 */
function DocumentPdf({
  kind,
  id,
  document,
  documentHash,
  contentHash,
  size,
  block,
}: {
  kind: 'quote' | 'invoice'
  id: string
  document: { id: string; checksum: string | null; filename: string } | null | undefined
  documentHash: string | null | undefined
  contentHash: string | null | undefined
  size: 'sm' | 'md'
  block?: boolean
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()

  const { open, viewer } = useFileViewer()
  const label = kind === 'quote' ? 'Quote' : 'Invoice'
  const pdfOf = (attachment: { id: string; checksum: string | null; filename: string }) =>
    attachmentViewerFile({ ...attachment, mimeType: 'application/pdf' }, `${label} · PDF`)

  // The new render opens in the viewer. A new tab here came after a round
  // trip, where Safari's popup blocker swallows it without a word.
  const onSaved = (result: { attachmentId: string; filename?: string }) => {
    void queryClient.invalidateQueries(
      kind === 'quote' ? trpc.quotes.pathFilter() : trpc.invoices.pathFilter(),
    )
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
    const file = pdfOf({
      id: result.attachmentId,
      checksum: 'stored',
      filename: result.filename ?? `${label}.pdf`,
    })
    open([file], file.id)
  }

  const saveQuote = useMutation(trpc.quotes.document.mutationOptions({ onSuccess: onSaved }))
  const saveInvoice = useMutation(trpc.invoices.document.mutationOptions({ onSuccess: onSaved }))
  const save = kind === 'quote' ? saveQuote : saveInvoice

  const state = documentPdfState({ document, documentHash, contentHash })
  const stored = document ? pdfOf(document) : null
  const url = stored?.url ?? null
  const mayView = can(kind === 'quote' ? 'QUOTE_VIEW' : 'INVOICE_VIEW')

  return (
    <>
      {viewer}
      {stored ? (
        <FileLink file={stored} onOpen={(file) => open([file], file.id)} className={buttonClass({ size, block })}>
          View the PDF
        </FileLink>
      ) : null}
      {mayView && (state !== 'current' || !url) ? (
        <Button
          size={size}
          variant={url ? 'ghost' : 'secondary'}
          block={block}
          loading={save.isPending}
          onClick={() => save.mutate({ id })}
        >
          {url ? 'Update the PDF' : 'Save PDF'}
        </Button>
      ) : null}
      {save.error ? <p className="text-xs text-red-700">{errorMessage(save.error)}</p> : null}
    </>
  )
}

/**
 * Billing a job that was quoted: the accepted lines, as a draft invoice.
 *
 * `billing.convertToInvoice` has always existed and had no caller, so the
 * office retyped every accepted quote into a blank invoice, and the billing
 * tab offered no invoice button at all once a quote was on the job. It takes
 * the package the customer chose and the add-ons they ticked, or the default
 * package of a quote nobody formally accepted. A blank invoice stays beside
 * it for work that went beyond the quote.
 */
function InvoiceFromQuote({
  job,
  quote,
  placement,
  lead,
}: {
  job: Job
  quote: Job['quotes'][number]
  placement: 'rail' | 'column'
  /** The tab's green action. Off while booking the work is the next step. */
  lead: boolean
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [blank, setBlank] = useState(false)

  const convert = useMutation(
    trpc.quotes.convertToInvoice.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.invoices.pathFilter())
        void queryClient.invalidateQueries(trpc.quotes.pathFilter())
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
      },
    }),
  )

  const accepted = quote.status === 'ACCEPTED'
  const amount = quote.acceptance?.acceptedTotalIncTax ?? quote.totalIncTax
  const stacked = placement === 'rail'

  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-lg p-3 ring-1 ring-inset',
        lead ? 'bg-brand-50 ring-brand-200' : 'bg-white ring-ink-200',
        placement === 'column' && 'my-3',
      )}
    >
      <div>
        <p className="font-display text-[14px] font-extrabold text-ink-900">
          {lead ? 'Ready to invoice' : 'Invoice it now'}
        </p>
        <p className="mt-0.5 text-[12.5px] leading-snug text-ink-600">
          {accepted
            ? `Bill what the customer accepted on ${quote.number}: ${money(amount)}.`
            : `Bill ${quote.number} as quoted: ${money(amount)}.`}
        </p>
      </div>
      <div className={cn('flex gap-2', stacked ? 'flex-col' : 'flex-wrap')}>
        <Button
          size={stacked ? 'md' : 'sm'}
          variant={lead ? 'primary' : 'secondary'}
          block={stacked}
          loading={convert.isPending}
          onClick={() => convert.mutate({ id: quote.id })}
        >
          {accepted ? 'Invoice the accepted quote' : 'Invoice this quote'}
        </Button>
        <Button size={stacked ? 'md' : 'sm'} variant="ghost" block={stacked} onClick={() => setBlank(true)}>
          Start a blank invoice
        </Button>
      </div>
      {convert.error ? <ErrorState message={errorMessage(convert.error)} /> : null}
      <RaiseDocumentPopup kind="invoice" job={job} open={blank} onClose={() => setBlank(false)} />
    </div>
  )
}

/** Accepted, and nothing in the diary: the work order's next move. */
function ReadyToBook({ onBook, placement }: { onBook: () => void; placement: 'rail' | 'column' }) {
  const stacked = placement === 'rail'
  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-lg bg-brand-50 p-3 ring-1 ring-brand-200 ring-inset',
        placement === 'column' && 'my-3',
      )}
    >
      <div>
        <p className="font-display text-[14px] font-extrabold text-ink-900">Accepted — book it in</p>
        <p className="mt-0.5 text-[12.5px] leading-snug text-ink-600">
          The job is a work order with nothing booked.
        </p>
      </div>
      <Button size={stacked ? 'md' : 'sm'} variant="primary" block={stacked} onClick={onBook}>
        Book it in
      </Button>
    </div>
  )
}

function QuoteBlock({
  quoteId,
  railTakesTotals,
  quoting,
  addressee,
}: {
  quoteId: string
  railTakesTotals: boolean
  /** The job is in the Quote stage, where a plain quote can always become a proposal. */
  quoting: boolean
  addressee: SendAddressee
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()
  const [sending, setSending] = useState(false)
  const [marking, setMarking] = useState(false)
  // Which quote the builder is open on. Not a boolean: making a sent quote a
  // proposal opens the builder on the new version, not on this one.
  const [building, setBuilding] = useState<string | null>(null)

  const { data: quote, isPending, error, refetch } = useQuery(trpc.quotes.byId.queryOptions({ id: quoteId }))

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.quotes.pathFilter())
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
  }

  const revise = useMutation(trpc.quotes.revise.mutationOptions({ onSuccess: invalidate }))
  const respond = useMutation(trpc.quotes.respond.mutationOptions({ onSuccess: invalidate }))
  const updateLines = useMutation(trpc.quotes.update.mutationOptions({ onSuccess: invalidate }))

  if (error) return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
  if (isPending) return <SkeletonRows rows={3} />

  const proposal = isProposalQuote(quote)
  const sendState = documentSendState(quote)
  const totals = proposal || acceptNeedsChoice(quote)
    ? proposalRailTotals(quote)
    : { rows: quoteTotals(quote, partsOf(quote.lines, quote)), total: ['Total', quote.totalIncTax] as [string, number] }
  const drafting = can('QUOTE_EDIT') && quote.status === 'DRAFT'
  // A sent or declined price list becomes a proposal as its next version: the
  // one the customer holds cannot change under them (decision 79).
  const convertible =
    !proposal &&
    !drafting &&
    quoting &&
    can('QUOTE_EDIT') &&
    quote.status !== 'ACCEPTED' &&
    quote.status !== 'SUPERSEDED'

  const makeProposal = () => {
    revise.mutate(
      { id: quote.id },
      { onSuccess: (revision) => setBuilding(revision.id) },
    )
  }

  return (
    <>
      <Panel padded={false}>
        <DocumentHeader
          label={proposal ? 'Proposal' : 'Quote'}
          number={quote.number}
          title={quote.title}
          tone={QUOTE_STATUS_TONE[quote.status] ?? 'neutral'}
          status={humanise(quote.status)}
          meta={
            quote.sentAt
              ? `Sent ${formatDate(quote.sentAt)}${quote.validUntil ? ` · valid to ${formatDate(quote.validUntil)}` : ''}`
              : 'Not sent yet'
          }
        >
          {drafting ? (
            <Button size="sm" variant={proposal ? 'primary' : 'secondary'} onClick={() => setBuilding(quote.id)}>
              {proposal ? 'Edit proposal' : 'Make it a proposal'}
            </Button>
          ) : convertible ? (
            <Button size="sm" variant="secondary" loading={revise.isPending} onClick={makeProposal}>
              Make it a proposal
            </Button>
          ) : null}
          {railTakesTotals ? null : (
            <QuoteActions
              quote={quote}
              onSend={() => setSending(true)}
              onMarkSent={() => setMarking(true)}
              revise={revise}
              respond={respond}
              sendState={sendState}
            />
          )}
        </DocumentHeader>

        {proposal ? (
          <ProposalLines quote={quote} />
        ) : (
          <LineTable
            lines={quote.lines}
            clientId={quote.job.clientId}
            editable={drafting}
            onSave={(next) => updateLines.mutate({ id: quote.id, lines: toLineInput(next) })}
            saving={updateLines.isPending}
          />
        )}

        {railTakesTotals ? null : <Totals rows={totals.rows} total={totals.total} />}

        {railTakesTotals ? null : (
          <div className="flex flex-col gap-1 border-t border-ink-100 px-4 py-3">
            <SendState state={sendState} kind="quote" />
            <ViewedNote quote={quote} />
          </div>
        )}
      </Panel>

      <AcceptancePanel quote={quote} />
      <DeclinedNote quote={quote} />

      {building ? (
        <ProposalBuilder
          kind="quote"
          open
          job={{
            id: quote.job.id,
            clientId: quote.job.clientId,
            categoryId: quote.job.categoryId,
            client: { name: quote.job.client.name },
          }}
          quoteId={building}
          onClose={() => setBuilding(null)}
        />
      ) : null}
      {revise.error ? <ErrorState message={errorMessage(revise.error)} /> : null}

      {quote.termsText && !proposal ? (
        <Panel>
          <PanelHeading title="Note on the quote" />
          <p className="text-[13px] leading-relaxed whitespace-pre-wrap text-ink-700">
            {quote.termsText}
          </p>
        </Panel>
      ) : null}

      <SendHistory sends={quote.sends} />

      {railTakesTotals ? null : (
        <>
          <SendDocumentPopup
            kind="quote"
            id={quote.id}
            number={quote.number}
            addressee={addressee}
            open={sending}
            onClose={() => setSending(false)}
          />
          <MarkSentPopup
            kind="quote"
            id={quote.id}
            number={quote.number}
            open={marking}
            onClose={() => setMarking(false)}
          />
        </>
      )}
    </>
  )
}

function QuoteRail({
  quoteId,
  addressee,
  lead,
}: {
  quoteId: string
  addressee: SendAddressee
  /** The stage's next step when it is not the quote's: invoicing it. */
  lead?: ReactNode
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [sending, setSending] = useState(false)
  const [marking, setMarking] = useState(false)

  const { data: quote, isPending, error, refetch } = useQuery(trpc.quotes.byId.queryOptions({ id: quoteId }))

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.quotes.pathFilter())
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
  }

  const revise = useMutation(trpc.quotes.revise.mutationOptions({ onSuccess: invalidate }))
  const respond = useMutation(trpc.quotes.respond.mutationOptions({ onSuccess: invalidate }))

  if (error) {
    return (
      <BillingRail>
        <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
      </BillingRail>
    )
  }
  if (isPending || !quote) {
    return (
      <BillingRail>
        <SkeletonRows rows={3} />
      </BillingRail>
    )
  }

  const sendState = documentSendState(quote)
  const totals =
    isProposalQuote(quote) || acceptNeedsChoice(quote)
      ? proposalRailTotals(quote)
      : { rows: quoteTotals(quote, partsOf(quote.lines, quote)), total: ['Total', quote.totalIncTax] as [string, number] }

  return (
    <BillingRail>
      {lead}
      <Totals rows={totals.rows} total={totals.total} />
      <SendState state={sendState} kind="quote" />
      <ViewedNote quote={quote} />
      <QuoteActions
        quote={quote}
        onSend={() => setSending(true)}
        onMarkSent={() => setMarking(true)}
        revise={revise}
        respond={respond}
        sendState={sendState}
        stacked
      />
      <SendDocumentPopup
        kind="quote"
        id={quote.id}
        number={quote.number}
        addressee={addressee}
        open={sending}
        onClose={() => setSending(false)}
      />
      <MarkSentPopup
        kind="quote"
        id={quote.id}
        number={quote.number}
        open={marking}
        onClose={() => setMarking(false)}
      />
    </BillingRail>
  )
}

function InvoiceBlock({
  invoiceId,
  railTakesTotals,
  yieldToProposal,
  addressee,
}: {
  invoiceId: string
  railTakesTotals: boolean
  /** On a phone the proposal sits above this document, so issuing steps back. */
  yieldToProposal?: boolean
  addressee: SendAddressee
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()
  const [paying, setPaying] = useState(false)
  const [sending, setSending] = useState(false)
  const [marking, setMarking] = useState(false)

  const {
    data: invoice,
    isPending,
    error,
    refetch,
  } = useQuery(trpc.invoices.byId.queryOptions({ id: invoiceId }))

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.invoices.pathFilter())
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
  }

  const issue = useMutation(trpc.invoices.issue.mutationOptions({ onSuccess: invalidate }))
  const updateLines = useMutation(trpc.invoices.update.mutationOptions({ onSuccess: invalidate }))

  if (error) return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
  if (isPending) return <SkeletonRows rows={3} />

  const parts = partsOf(invoice.lines, invoice)
  const owing = invoice.totalIncTax - invoice.amountPaid
  const sendState = documentSendState(invoice)
  const actions = railTakesTotals ? null : (
    <InvoiceActions
      invoice={invoice}
      issue={issue}
      onRecord={() => setPaying(true)}
      onEmail={() => setSending(true)}
      onMarkSent={() => setMarking(true)}
      sendState={sendState}
      yieldsPrimary={yieldToProposal}
    />
  )

  return (
    <>
      <Panel padded={false}>
        <DocumentHeader
          label="Invoice"
          number={invoice.number}
          title={invoice.job?.title ?? null}
          tone={INVOICE_STATUS_TONE[invoice.status] ?? 'neutral'}
          status={humanise(invoice.status)}
          meta={
            invoice.issuedAt
              ? `Issued ${formatDate(invoice.issuedAt)}${invoice.dueAt ? ` · due ${formatDate(invoice.dueAt)}` : ''}`
              : 'Draft — not issued'
          }
        >
          {actions}
        </DocumentHeader>

        <LineTable
          lines={invoice.lines}
          clientId={invoice.client?.id ?? null}
          editable={can('INVOICE_EDIT') && invoiceLinesWritable(invoice)}
          onSave={(next) => updateLines.mutate({ id: invoice.id, lines: toLineInput(next) })}
          saving={updateLines.isPending}
          lockedReason={
            can('INVOICE_EDIT') && !invoiceLinesWritable(invoice)
              ? 'Lines cannot be changed once money has been received.'
              : null
          }
        />

        {railTakesTotals ? null : (
          <Totals
            rows={[
              ['Subtotal', parts.subtotal],
              [parts.taxLabel, parts.tax],
              ['Invoiced', invoice.totalIncTax],
              ['Paid', invoice.amountPaid],
            ]}
            total={['Owing', owing]}
          />
        )}

        {railTakesTotals ? null : (
          <div className="flex flex-col gap-1 border-t border-ink-100 px-4 py-3">
            <SendState state={sendState} kind="invoice" />
            <LatestSendTracking sends={invoice.sends} />
          </div>
        )}
      </Panel>

      <SendHistory sends={invoice.sends} />

      {railTakesTotals ? null : (
        <>
          <PaymentPopup
            invoiceId={invoice.id}
            number={invoice.number}
            owing={owing}
            open={paying}
            onClose={() => setPaying(false)}
          />
          <SendDocumentPopup
            kind="invoice"
            id={invoice.id}
            number={invoice.number}
            addressee={addressee}
            open={sending}
            onClose={() => setSending(false)}
          />
          <MarkSentPopup
            kind="invoice"
            id={invoice.id}
            number={invoice.number}
            open={marking}
            onClose={() => setMarking(false)}
          />
        </>
      )}

      <Panel>
        <PanelHeading
          title="Payments"
          hint={invoice.payments.length > 0 ? `${money(invoice.amountPaid)} received` : undefined}
        />
        {invoice.payments.length === 0 ? (
          <p className="text-[13px] text-ink-400">Nothing received against this invoice.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-ink-100">
            {invoice.payments.map((payment) => (
              <li key={payment.id} className="flex items-center gap-2.5 py-2 text-[13px]">
                <span className="w-[88px] shrink-0 tabular-nums text-ink-600">
                  {formatDate(payment.receivedAt)}
                </span>
                <span className="truncate text-ink-700">{humanise(payment.method)}</span>
                <span className="ml-auto shrink-0 font-medium tabular-nums text-ink-900">
                  {money(payment.amount)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </>
  )
}

function InvoiceRail({
  invoiceId,
  addressee,
  proposal,
}: {
  invoiceId: string
  addressee: SendAddressee
  /** The Quote-stage lead, rendered above the invoice's own actions. */
  proposal?: ReactNode
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [paying, setPaying] = useState(false)
  const [sending, setSending] = useState(false)
  const [marking, setMarking] = useState(false)

  const {
    data: invoice,
    error,
    refetch,
  } = useQuery(trpc.invoices.byId.queryOptions({ id: invoiceId }))

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.invoices.pathFilter())
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
  }

  const issue = useMutation(trpc.invoices.issue.mutationOptions({ onSuccess: invalidate }))

  // The proposal does not wait on the invoice. A slow or failed fetch of the
  // draft must not hide the action the stage is asking for, and the control
  // stays mounted so opening it is not thrown away when the figures arrive.
  const ready = invoice && !error ? invoice : null
  const parts = ready ? partsOf(ready.lines, ready) : null
  const owing = ready ? ready.totalIncTax - ready.amountPaid : 0
  const sendState = ready ? documentSendState(ready) : null

  return (
    <BillingRail>
      {error ? (
        <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
      ) : !ready || !parts ? (
        <SkeletonRows rows={3} />
      ) : (
        <>
          <Totals
            rows={[
              ['Subtotal', parts.subtotal],
              [parts.taxLabel, parts.tax],
              ['Invoiced', ready.totalIncTax],
              ['Paid', ready.amountPaid],
            ]}
            total={['Owing', owing]}
          />
          {sendState ? <SendState state={sendState} kind="invoice" /> : null}
          <LatestSendTracking sends={ready.sends} />
        </>
      )}
      {proposal}
      {ready && sendState ? (
        <>
          <InvoiceActions
            invoice={ready}
            issue={issue}
            onRecord={() => setPaying(true)}
            onEmail={() => setSending(true)}
            onMarkSent={() => setMarking(true)}
            sendState={sendState}
            yieldsPrimary={Boolean(proposal)}
            stacked
          />
          <PaymentPopup
            invoiceId={ready.id}
            number={ready.number}
            owing={owing}
            open={paying}
            onClose={() => setPaying(false)}
          />
          <SendDocumentPopup
            kind="invoice"
            id={ready.id}
            number={ready.number}
            addressee={addressee}
            open={sending}
            onClose={() => setSending(false)}
          />
          <MarkSentPopup
            kind="invoice"
            id={ready.id}
            number={ready.number}
            open={marking}
            onClose={() => setMarking(false)}
          />
        </>
      ) : null}
    </BillingRail>
  )
}

function BillingRail({ children }: { children: ReactNode }) {
  return (
    <aside
      aria-label="Billing"
      className="flex shrink-0 flex-col gap-3 border-t border-ink-200 bg-ink-50 px-5 py-4 lg:w-[288px] lg:overflow-y-auto lg:border-t-0"
    >
      {children}
    </aside>
  )
}

function QuoteActions({
  quote,
  onSend,
  onMarkSent,
  revise,
  respond,
  sendState,
  stacked,
}: {
  quote: RouterOutputs['quotes']['byId']
  /** Opens the send sheet. Sending is a confirmation step, not a click. */
  onSend: () => void
  /** Records a copy that went out on paper. */
  onMarkSent: () => void
  revise: { isPending: boolean; mutate: (input: { id: string }) => void }
  respond: { isPending: boolean; mutate: (input: { id: string; outcome: 'ACCEPTED' }) => void }
  sendState: DocumentSendState
  stacked?: boolean
}) {
  const { can } = useSession()
  const [accepting, setAccepting] = useState(false)
  const settled = quote.status === 'ACCEPTED' || quote.status === 'DECLINED'
  // Accepted is final and superseded is history: the server refuses to send
  // or revise either, so neither is offered.
  const closed = quote.status === 'ACCEPTED' || quote.status === 'SUPERSEDED'
  const size = stacked ? 'md' : 'sm'

  // A stale quote's most important action is sending the corrected one, so it
  // takes the primary slot from Accept. Accepting a price the customer has not
  // been shown is the mistake this is here to prevent.
  const sendIsUrgent = sendState.stale

  // With options or extras on it, a yes has to say which — that is a popup,
  // not a click.
  const accept = () =>
    acceptNeedsChoice(quote) ? setAccepting(true) : respond.mutate({ id: quote.id, outcome: 'ACCEPTED' })

  // A quote nobody has been given is the same case as a stale one: the next
  // thing is to send it, and a yes on the phone is the exception. So Send
  // leads and Accept stays beside it as the quieter action, rather than the
  // green button being a price the customer has not seen. Decision 103.
  const unsent = quote.sentAt === null && !settled && !closed
  const sendLeads = sendIsUrgent || unsent

  const acceptButton =
    can('QUOTE_EDIT') && !settled && !closed && !sendIsUrgent ? (
      <Button
        size={size}
        variant={unsent ? 'secondary' : 'primary'}
        block={stacked}
        loading={respond.isPending}
        onClick={accept}
      >
        {stacked ? 'Accept and start the work' : 'Mark accepted'}
      </Button>
    ) : null
  const sendButton =
    can('QUOTE_SEND') && !closed ? (
      <Button
        size={size}
        variant={sendLeads ? 'primary' : 'secondary'}
        block={stacked}
        onClick={onSend}
      >
        {sendIsUrgent ? 'Send the new figures' : quote.sentAt ? 'Send again' : 'Send'}
      </Button>
    ) : null

  return (
    <div className={cn('flex flex-wrap gap-1.5', stacked && 'flex-col gap-2')}>
      {sendLeads ? sendButton : acceptButton}
      {sendLeads ? acceptButton : sendButton}
      <OfficeAcceptPopup quote={quote} open={accepting} onClose={() => setAccepting(false)} />
      <DocumentPdf
        kind="quote"
        id={quote.id}
        document={quote.document}
        documentHash={quote.documentHash}
        contentHash={quote.contentHash}
        size={size}
        block={stacked}
      />
      {can('QUOTE_SEND') && !closed ? (
        <Button size={size} variant="ghost" block={stacked} onClick={onMarkSent}>
          Sent another way
        </Button>
      ) : null}
      {can('QUOTE_EDIT') && !sendIsUrgent && !closed ? (
        <Button
          size={size}
          block={stacked}
          loading={revise.isPending}
          onClick={() => revise.mutate({ id: quote.id })}
        >
          {stacked ? 'Revise the price' : 'New version'}
        </Button>
      ) : null}
    </div>
  )
}

function InvoiceActions({
  invoice,
  issue,
  onRecord,
  onEmail,
  onMarkSent,
  sendState,
  yieldsPrimary,
  stacked,
}: {
  invoice: {
    id: string
    status: string
    sentAt: Date | string | null
    document?: { id: string; checksum: string | null; filename: string } | null
    documentHash?: string | null
    contentHash?: string | null
  }
  issue: { isPending: boolean; mutate: (input: { id: string }) => void }
  onRecord: () => void
  onEmail: () => void
  onMarkSent: () => void
  sendState: DocumentSendState
  /** A proposal is the lead, so issuing and taking payment are no longer the green button. */
  yieldsPrimary?: boolean
  stacked?: boolean
}) {
  const { can } = useSession()
  const size = stacked ? 'md' : 'sm'
  const lead = yieldsPrimary ? 'secondary' : 'primary'

  // A draft has a provisional number and a void invoice is not owed, so
  // neither is sendable. The server refuses both as well.
  const sendable = invoice.status !== 'DRAFT' && invoice.status !== 'VOID'

  // The button that used to say "Email again" about figures the customer had
  // never seen. This is the whole reason the send rows exist.
  const sendIsUrgent = sendState.stale

  return (
    <div className={cn('flex flex-wrap gap-1.5', stacked && 'flex-col gap-2')}>
      {/* Take payment is in the mockup and not here: no gateway is connected. */}
      {can('INVOICE_SEND') && sendable ? (
        <Button
          size={size}
          variant={sendIsUrgent ? 'primary' : 'secondary'}
          block={stacked}
          onClick={onEmail}
        >
          {sendIsUrgent
            ? 'Email the new figures'
            : invoice.sentAt
              ? 'Email again'
              : 'Email invoice'}
        </Button>
      ) : null}
      <DocumentPdf
        kind="invoice"
        id={invoice.id}
        document={invoice.document}
        documentHash={invoice.documentHash}
        contentHash={invoice.contentHash}
        size={size}
        block={stacked}
      />
      {can('INVOICE_SEND') && sendable ? (
        <Button size={size} variant="ghost" block={stacked} onClick={onMarkSent}>
          Sent another way
        </Button>
      ) : null}
      {can('INVOICE_ISSUE') && invoice.status === 'DRAFT' ? (
        <Button
          size={size}
          variant={lead}
          block={stacked}
          loading={issue.isPending}
          onClick={() => issue.mutate({ id: invoice.id })}
        >
          Issue invoice
        </Button>
      ) : null}
      {can('PAYMENT_RECORD') && invoice.status !== 'DRAFT' ? (
        <Button size={size} variant={lead} block={stacked} onClick={onRecord}>
          Record payment
        </Button>
      ) : null}
    </div>
  )
}

function DocumentHeader({
  label,
  number,
  title,
  tone,
  status,
  meta,
  children,
}: {
  label: string
  number: string
  title: string | null
  tone: Tone
  status: string
  meta: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-ink-100 px-4 py-3.5">
      <Label>{label}</Label>
      <span className="font-mono text-[13px] font-semibold text-ink-600">{number}</span>
      {title ? <span className="text-sm font-semibold text-ink-900">{title}</span> : null}
      <Badge tone={tone}>{status}</Badge>
      <span className="text-xs tabular-nums text-ink-500">{meta}</span>
      <div className="ml-auto flex flex-wrap gap-1.5">{children}</div>
    </div>
  )
}

/**
 * What the table reads, rather than either row type.
 *
 * Quote lines and invoice lines are separate Prisma models that happen to
 * agree on everything except `isOptional`, so neither is assignable to the
 * other and typing this as one of them would make the other a cast. The
 * quantity is a `Decimal`, which is only ever rendered.
 */
interface Line {
  id: string
  description: string
  quantity: { toString: () => string }
  unitPrice: number
  /** Kept when the price was typed inc GST; an edit to the unit price here clears it. */
  unitPriceIncTax?: number | null
  taxRateBasisPoints: number
  lineTotalExTax: number
  lineTax: number
  lineTotalIncTax: number
  material: { name: string; code?: string | null } | null
  taxRate: { name: string } | null
  taxRateId?: string | null
  unitCost?: number
  discountBasisPoints?: number
  isOptional?: boolean
  sortOrder?: number
  materialId?: string | null
}

const COLUMNS = 'grid grid-cols-[1fr_64px_96px_72px_104px] gap-3'
const EDIT_COLUMNS = 'grid grid-cols-[1fr_64px_96px_72px_104px_28px] gap-3'

/** The seed's GST 10% rate. Copied onto a new line so a free-text row is taxed. */
const GST = {
  taxRateId: 'taxrate_gst_default',
  taxRateBasisPoints: 1000,
  taxRate: { name: 'GST 10%' },
}

function invoiceLinesWritable(invoice: { status: string; amountPaid: number }): boolean {
  if (invoice.status === 'VOID' || invoice.status === 'WRITTEN_OFF') return false
  if (invoice.amountPaid > 0 || invoice.status === 'PAID' || invoice.status === 'PARTIALLY_PAID') {
    return false
  }
  return invoice.status === 'DRAFT' || invoice.status === 'ISSUED' || invoice.status === 'OVERDUE'
}

function LineTable({
  lines,
  clientId,
  editable = false,
  onSave,
  saving,
  lockedReason,
}: {
  lines: Line[]
  /** Prices a catalog pick at this client's tier. */
  clientId: string | null
  editable?: boolean
  onSave?: (lines: Line[]) => void
  saving?: boolean
  lockedReason?: string | null
}) {
  const { can } = useSession()
  const [drafts, setDrafts] = useState<Line[]>(lines)
  const [creating, setCreating] = useState<{ index: number; name: string } | null>(null)

  useEffect(() => {
    setDrafts(lines)
  }, [lines])

  const rows = editable ? drafts : lines
  const columns = editable ? EDIT_COLUMNS : COLUMNS

  const patch = (index: number, next: Partial<Line>) =>
    setDrafts((current) =>
      current.map((row, i) => (i === index ? previewLine({ ...row, ...next }) : row)),
    )

  // An item with no tax rate of its own keeps the line's, the way
  // `catalog.linesForJob` falls back to the default rate.
  const pick = (index: number, item: CatalogItem) =>
    patch(index, {
      description: item.name,
      materialId: item.id,
      material: { name: item.name, code: item.code },
      unitPrice: item.unitPrice,
      unitPriceIncTax: null,
      unitCost: item.unitCost,
      ...(item.taxRateId
        ? {
            taxRateId: item.taxRateId,
            taxRateBasisPoints: item.taxRateBasisPoints,
            taxRate: item.taxRateName ? { name: item.taxRateName } : null,
          }
        : {}),
    })

  if (!editable && lines.length === 0) {
    return <EmptyState title="No lines" description="Nothing has been priced on this document." />
  }

  return (
    <div>
      <div
        className={`${columns} border-b border-ink-200 bg-ink-100 px-4 py-2 text-[10px] font-bold tracking-[0.06em] text-ink-500 uppercase`}
      >
        <span>Item</span>
        <span className="text-right">Qty</span>
        <span className="text-right">Unit</span>
        <span className="text-right">GST</span>
        <span className="text-right">Amount</span>
        {editable ? <span className="sr-only">Remove</span> : null}
      </div>

      {rows.map((line, index) => (
        <div
          key={line.id}
          className={`${columns} ${editable ? 'items-start' : 'items-baseline'} border-b border-ink-100 px-4 py-2.5 text-[13px]`}
        >
          {editable ? (
            <>
              <div className="flex min-w-0 flex-col gap-0.5">
                <CatalogLineField
                  index={index}
                  value={line.description}
                  clientId={clientId}
                  searchable={can('CATALOG_VIEW')}
                  canCreate={can('CATALOG_EDIT')}
                  // Emptying the box is starting the line again, so it lets go of the item.
                  onChange={(description) =>
                    patch(index, description.trim() ? { description } : { description, materialId: null, material: null })
                  }
                  onPick={(item) => pick(index, item)}
                  onCreate={(name) => setCreating({ index, name })}
                />
                <LineItemMeta line={line} />
              </div>
              <input
                aria-label={`Line ${index + 1} quantity`}
                value={quantityOf(line)}
                onChange={(event) =>
                  patch(index, { quantity: { toString: () => event.target.value } })
                }
                className="border-0 bg-transparent text-right tabular-nums text-ink-700 outline-none"
              />
              <input
                aria-label={`Line ${index + 1} unit price`}
                inputMode="decimal"
                value={(line.unitPrice / 100).toString()}
                onChange={(event) => {
                  const cents = Math.round(Number(event.target.value) * 100)
                  if (!Number.isFinite(cents)) return
                  patch(index, { unitPrice: cents, unitPriceIncTax: null })
                }}
                className="border-0 bg-transparent text-right tabular-nums text-ink-700 outline-none"
              />
              <span className="text-right text-xs text-ink-500">
                {line.taxRateBasisPoints > 0 ? 'incl.' : '—'}
              </span>
              <span className="text-right font-medium tabular-nums text-ink-900">
                {money(line.lineTotalIncTax)}
              </span>
              <button
                type="button"
                aria-label={`Remove line ${index + 1}`}
                onClick={() => setDrafts((current) => current.filter((_, i) => i !== index))}
                className="flex size-7 items-center justify-center text-ink-400 hover:text-ink-800"
              >
                <Icon name="close" className="size-3.5" />
              </button>
            </>
          ) : (
            <>
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate font-medium text-ink-900">{line.description}</span>
                <LineItemMeta line={line} />
              </span>
              <span className="text-right tabular-nums text-ink-700">{quantityOf(line)}</span>
              <span className="text-right tabular-nums text-ink-700">{money(line.unitPrice)}</span>
              <span className="text-right text-xs text-ink-500">
                {line.taxRateBasisPoints > 0 ? 'incl.' : '—'}
              </span>
              <span className="text-right font-medium tabular-nums text-ink-900">
                {money(line.lineTotalIncTax)}
              </span>
            </>
          )}
        </div>
      ))}

      {editable ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 px-4 py-2">
          <Button
            type="button"
            size="sm"
            onClick={() => setDrafts((current) => [...current, blankLine(current.length)])}
          >
            Add a line
          </Button>
          {onSave ? (
            <Button
              type="button"
              size="sm"
              // Green only once something has been typed: an untouched table
              // beside the rail's Send was two primary buttons, and the one
              // that mattered was not this one. `drafts` is the `lines` array
              // itself until an edit makes a new one.
              variant={drafts === lines ? 'secondary' : 'primary'}
              loading={saving}
              onClick={() => onSave(drafts.filter((row) => row.description.trim()).map(previewLine))}
            >
              Save lines
            </Button>
          ) : null}
        </div>
      ) : null}

      {lockedReason ? (
        <p className="px-4 py-2.5 text-[12.5px] text-ink-500">{lockedReason}</p>
      ) : null}

      {editable ? (
        <CatalogItemPopup
          open={creating !== null}
          initialName={creating?.name ?? ''}
          onClose={() => setCreating(null)}
          onCreated={(item) => {
            if (creating) pick(creating.index, item)
          }}
        />
      ) : null}
    </div>
  )
}

/**
 * The catalog item under the line: its code, and its name when the line's
 * words have moved away from it.
 *
 * The code is the office's handle on the item and is never on the document —
 * the PDF, the invoice page and the email print the description only.
 */
function LineItemMeta({ line }: { line: Line }) {
  const code = line.material?.code
  const name = detailOf(line)
  if (!code && !name) return null

  return (
    <span className="flex min-w-0 items-baseline gap-1.5 text-xs text-ink-500">
      {code ? (
        <span className="shrink-0 font-mono text-[11px]" title="Item code. Not printed on the document.">
          <span className="sr-only">Item code </span>
          {code}
        </span>
      ) : null}
      {name ? <span className="truncate">{name}</span> : null}
    </span>
  )
}

function blankLine(sortOrder: number): Line {
  return previewLine({
    id: `new_${sortOrder}_${Date.now()}`,
    description: '',
    quantity: { toString: () => '1' },
    unitPrice: 0,
    taxRateBasisPoints: GST.taxRateBasisPoints,
    lineTotalExTax: 0,
    lineTax: 0,
    lineTotalIncTax: 0,
    material: null,
    taxRate: GST.taxRate,
    taxRateId: GST.taxRateId,
    unitCost: 0,
    discountBasisPoints: 0,
    sortOrder,
  })
}

/**
 * Live amount while somebody is typing. The server recalculates on save with
 * `money.calculateLine`; this is the same half-up arithmetic in integers so
 * a typed 2.5 × $9.50 does not flash a float on the way through.
 */
function previewLine(line: Line): Line {
  const quantity = Number(quantityOf(line))
  if (!Number.isFinite(quantity)) {
    return { ...line, lineTotalExTax: 0, lineTax: 0, lineTotalIncTax: 0 }
  }

  const scaled = Math.round(quantity * 10_000)

  if (line.unitPriceIncTax != null) {
    const inc = Math.round((scaled * line.unitPriceIncTax) / 10_000)
    const exOfInc = Math.round((inc * 10_000) / (10_000 + line.taxRateBasisPoints))
    return { ...line, lineTotalExTax: exOfInc, lineTax: inc - exOfInc, lineTotalIncTax: inc }
  }

  const ex = Math.round((scaled * line.unitPrice) / 10_000)
  const tax = Math.round((ex * line.taxRateBasisPoints) / 10_000)

  return { ...line, lineTotalExTax: ex, lineTax: tax, lineTotalIncTax: ex + tax }
}

function toLineInput(lines: Line[]) {
  return lines.map((line, index) => ({
    description: line.description,
    quantity: line.quantity.toString(),
    unitPrice: line.unitPrice,
    unitPriceIncTax: line.unitPriceIncTax ?? null,
    unitCost: line.unitCost ?? 0,
    discountBasisPoints: line.discountBasisPoints ?? 0,
    taxRateId: line.taxRateId ?? null,
    isOptional: line.isOptional ?? false,
    sortOrder: line.sortOrder ?? index,
    materialId: line.materialId ?? null,
  }))
}

/**
 * The second line under the item.
 *
 * The description is copied off the catalog item when the line is created and
 * then edited freely, so the two agree far more often than not. Repeating the
 * same words in smaller grey type is worse than leaving the row alone.
 */
function detailOf(line: Line): string | null {
  const name = line.material?.name
  return name && name !== line.description ? name : null
}

/** Quantity is a Decimal, and 4.000 is a 4 to everyone reading an invoice. */
function quantityOf(line: Line): string {
  const text = String(line.quantity)
  const value = Number(text)
  return Number.isFinite(value) ? String(value) : text
}

/**
 * Subtotal and tax, from the lines where there are lines.
 *
 * The ServiceM8 import wrote a document total and nothing else, so seven
 * years of invoices carry a real `totalIncTax` beside a `subtotalExTax` of
 * zero. Printing that zero states a figure nobody calculated, and next to a
 * four hundred dollar total it reads as a bug in the arithmetic rather than a
 * gap in the data. Null means "not known", and the panel draws a dash.
 */
function partsOf(
  lines: Line[],
  header: { subtotalExTax: number; totalTax: number; totalIncTax: number },
): { subtotal: number | null; tax: number | null; taxLabel: string } {
  const taxLabel = taxLabelFor(lines)

  if (lines.length > 0) {
    return {
      subtotal: lines.reduce((sum, line) => sum + line.lineTotalExTax, 0),
      tax: lines.reduce((sum, line) => sum + line.lineTax, 0),
      taxLabel,
    }
  }

  // A zero part under a non-zero total was never worked out. A document that
  // genuinely totals nothing has zero parts, and zero is the truth there.
  const known = (value: number) => (value === 0 && header.totalIncTax !== 0 ? null : value)

  return { subtotal: known(header.subtotalExTax), tax: known(header.totalTax), taxLabel }
}

/** "GST 10%" when the lines agree on one rate, which they almost always do. */
function taxLabelFor(lines: Line[]): string {
  const names = new Set(
    lines
      .filter((line) => line.taxRateBasisPoints > 0)
      .map((line) => line.taxRate?.name)
      .filter((name): name is string => Boolean(name)),
  )

  return names.size === 1 ? [...names][0]! : 'Tax'
}

function quoteTotals(
  quote: { discountAmount: number },
  parts: { subtotal: number | null; tax: number | null; taxLabel: string },
): Array<[string, number | null]> {
  const rows: Array<[string, number | null]> = [
    ['Subtotal', parts.subtotal],
    [parts.taxLabel, parts.tax],
  ]
  if (quote.discountAmount) rows.push(['Discount', -quote.discountAmount])
  return rows
}

function Totals({
  rows,
  total,
}: {
  rows: Array<[string, number | null]>
  total: [string, number]
}) {
  return (
    <dl className="flex flex-col gap-[7px] px-4 py-3.5">
      {rows.map(([label, value]) => (
        <div key={label} className="flex justify-between text-[12.5px]">
          <dt className="text-ink-500">{label}</dt>
          <dd className="tabular-nums text-ink-800">{value === null ? '—' : money(value)}</dd>
        </div>
      ))}
      <div className="flex items-baseline justify-between border-t border-ink-100 pt-[7px]">
        <dt className="text-[13.5px] font-bold text-ink-900">{total[0]}</dt>
        <dd className="text-[18px] font-bold tabular-nums text-ink-900">{money(total[1])}</dd>
      </div>
    </dl>
  )
}

/** A sent copy of a quote or invoice, named by the version it was. */
function sentCopyFile(attachment: { id: string; checksum: string | null; filename: string }, version: number): ViewerFile {
  return attachmentViewerFile({ ...attachment, mimeType: 'application/pdf' }, `v${version} · the copy they got`)
}
